/**
 * 图片压缩工具
 * 
 * 功能：
 * - 将图片压缩到指定大小以下（默认 500KB）
 * - 支持 JPEG 和 PNG 格式
 * - 使用 Canvas API 进行压缩
 * - 自动调整图片尺寸和质量
 * 
 * @author AI Assistant
 * @version 1.0.0
 */

// 默认配置
const DEFAULT_CONFIG = {
    maxSizeMB: 0.5,         // 最大文件大小（MB）
    maxWidthOrHeight: 1280, // 最大宽度或高度
    initialQuality: 0.9,    // 初始压缩质量
    minQuality: 0.5,        // 最小压缩质量
    qualityStep: 0.05,      // 质量递减步长
};

/**
 * 压缩图片到指定大小以下
 * 
 * @param {File} file - 原始图片文件
 * @param {Object} options - 压缩选项
 * @param {number} options.maxSizeMB - 最大文件大小（MB），默认 1
 * @param {number} options.maxWidthOrHeight - 最大宽度或高度，默认 1920
 * @param {number} options.initialQuality - 初始压缩质量，默认 0.92
 * @returns {Promise<File>} 压缩后的文件
 */
export async function compressImage(file, options = {}) {
    const config = { ...DEFAULT_CONFIG, ...options };
    const maxSizeBytes = config.maxSizeMB * 1024 * 1024;

    // 如果文件已经小于目标大小，直接返回
    if (file.size <= maxSizeBytes) {
        console.log(`[ImageCompressor] 文件已小于 ${config.maxSizeMB}MB，无需压缩`);
        return file;
    }

    console.log(`[ImageCompressor] 开始压缩: ${file.name}`);
    console.log(`[ImageCompressor] 原始大小: ${(file.size / 1024 / 1024).toFixed(2)}MB`);

    try {
        // 1. 加载图片
        const img = await loadImage(file);

        // 2. 计算目标尺寸
        const { width, height } = calculateDimensions(
            img.width,
            img.height,
            config.maxWidthOrHeight
        );

        console.log(`[ImageCompressor] 原始尺寸: ${img.width}x${img.height}`);
        console.log(`[ImageCompressor] 目标尺寸: ${width}x${height}`);

        // 3. 创建 Canvas 并绘制
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // 4. 逐步降低质量直到满足大小要求
        let quality = config.initialQuality;
        let compressedFile = await canvasToFile(canvas, file.name, file.type, quality);

        while (compressedFile.size > maxSizeBytes && quality > config.minQuality) {
            quality -= config.qualityStep;
            console.log(`[ImageCompressor] 尝试质量: ${(quality * 100).toFixed(0)}%`);
            compressedFile = await canvasToFile(canvas, file.name, file.type, quality);
        }

        // 5. 如果仍然超过大小，进一步缩小尺寸
        if (compressedFile.size > maxSizeBytes) {
            console.log(`[ImageCompressor] 质量压缩不足，开始缩小尺寸...`);
            compressedFile = await compressWithDimensionReduction(
                img,
                file.name,
                file.type,
                maxSizeBytes,
                width,
                height
            );
        }

        console.log(`[ImageCompressor] 压缩完成: ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
        console.log(`[ImageCompressor] 压缩率: ${((1 - compressedFile.size / file.size) * 100).toFixed(1)}%`);

        return compressedFile;
    } catch (error) {
        console.error('[ImageCompressor] 压缩失败:', error);
        // 压缩失败时返回原文件
        return file;
    }
}

/**
 * 加载图片
 * @param {File} file - 图片文件
 * @returns {Promise<HTMLImageElement>} 图片元素
 */
function loadImage(file) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(img.src);
            resolve(img);
        };
        img.onerror = () => {
            URL.revokeObjectURL(img.src);
            reject(new Error('图片加载失败'));
        };
        img.src = URL.createObjectURL(file);
    });
}

/**
 * 计算保持宽高比的目标尺寸
 * @param {number} originalWidth - 原始宽度
 * @param {number} originalHeight - 原始高度
 * @param {number} maxSize - 最大边长
 * @returns {{ width: number, height: number }} 目标尺寸
 */
function calculateDimensions(originalWidth, originalHeight, maxSize) {
    if (originalWidth <= maxSize && originalHeight <= maxSize) {
        return { width: originalWidth, height: originalHeight };
    }

    let width, height;
    if (originalWidth > originalHeight) {
        width = maxSize;
        height = Math.round((originalHeight / originalWidth) * maxSize);
    } else {
        height = maxSize;
        width = Math.round((originalWidth / originalHeight) * maxSize);
    }

    return { width, height };
}

/**
 * 将 Canvas 转换为 File
 * @param {HTMLCanvasElement} canvas - Canvas 元素
 * @param {string} fileName - 文件名
 * @param {string} mimeType - MIME 类型
 * @param {number} quality - 压缩质量 (0-1)
 * @returns {Promise<File>} 转换后的文件
 */
function canvasToFile(canvas, fileName, mimeType, quality) {
    return new Promise((resolve, reject) => {
        // PNG 不支持质量参数，转换为 JPEG
        const outputType = mimeType === 'image/png' ? 'image/jpeg' : mimeType;
        const outputFileName = mimeType === 'image/png'
            ? fileName.replace(/\.png$/i, '.jpg')
            : fileName;

        try {
            canvas.toBlob(
                (blob) => {
                    if (!blob) {
                        console.error('[ImageCompressor] toBlob 返回为空，可能是 Canvas 尺寸过大或内存不足');
                        reject(new Error('图片处理失败: 内存不足或尺寸过大'));
                        return;
                    }
                    const file = new File([blob], outputFileName, {
                        type: outputType,
                        lastModified: Date.now()
                    });
                    resolve(file);
                },
                outputType,
                quality
            );
        } catch (error) {
            console.error('[ImageCompressor] canvas.toBlob 抛出异常:', error);
            reject(error);
        }
    });
}

/**
 * 通过进一步缩小尺寸来压缩图片
 * @param {HTMLImageElement} img - 原始图片元素
 * @param {string} fileName - 文件名
 * @param {string} mimeType - MIME 类型
 * @param {number} maxSizeBytes - 目标最大字节数
 * @param {number} initialWidth - 初始宽度
 * @param {number} initialHeight - 初始高度
 * @returns {Promise<File>} 压缩后的文件
 */
async function compressWithDimensionReduction(
    img,
    fileName,
    mimeType,
    maxSizeBytes,
    initialWidth,
    initialHeight
) {
    let scale = 0.9;
    let width = initialWidth;
    let height = initialHeight;
    let compressedFile;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    while (scale > 0.3) {
        width = Math.round(initialWidth * scale);
        height = Math.round(initialHeight * scale);

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        compressedFile = await canvasToFile(canvas, fileName, mimeType, 0.8);

        if (compressedFile.size <= maxSizeBytes) {
            console.log(`[ImageCompressor] 尺寸缩放到 ${(scale * 100).toFixed(0)}% 后满足要求`);
            break;
        }

        scale -= 0.1;
    }

    return compressedFile;
}

/**
 * 批量压缩图片
 * @param {File[]} files - 图片文件数组
 * @param {Object} options - 压缩选项
 * @param {Function} onProgress - 进度回调 (current, total, file)
 * @returns {Promise<File[]>} 压缩后的文件数组
 */
export async function batchCompressImages(files, options = {}, onProgress) {
    const compressedFiles = [];
    const total = files.length;

    for (let i = 0; i < files.length; i++) {
        const file = files[i];

        if (onProgress) {
            onProgress(i, total, file);
        }

        const compressedFile = await compressImage(file, options);
        compressedFiles.push(compressedFile);
    }

    if (onProgress) {
        onProgress(total, total, null);
    }

    return compressedFiles;
}

/**
 * 检查文件是否需要压缩
 * @param {File} file - 文件
 * @param {number} maxSizeMB - 最大大小（MB）
 * @returns {boolean} 是否需要压缩
 */
export function needsCompression(file, maxSizeMB = 1) {
    return file.size > maxSizeMB * 1024 * 1024;
}

/**
 * 获取友好的文件大小显示
 * @param {number} bytes - 字节数
 * @returns {string} 友好显示
 */
export function formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
}

export default {
    compressImage,
    batchCompressImages,
    needsCompression,
    formatFileSize
};
